<template>
  <div class="BirthDatesComponent">
    <FormField
      v-for="(insured, index) in insureds"
      :key="index"
      :statement="birthDateStatement(insured as Insured, index)"
    >
      <BirthDatePickerComponent
        :key="props.startDate?.getTime()"
        :legend="t('rater.common.more-info.birth-date.legend')"
        :id="`birthDate-${index}`"
        v-model="insured.userDetails.birthDate"
        :rules="rulesToApply(insured.role)"
      />
    </FormField>
  </div>
</template>

<script lang="ts" setup>
import type { ValidationRuleType } from '@/modules/core/models/types'
import { useI18n } from 'vue-i18n'
import { ForWhomEnum, UserRoleEnum } from '@/modules/core/models/enums'
import Insured from '@/modules/common/common/models/APP/Insured'
import BirthDatePickerComponent from '@/modules/core/components/formFields/BirthDatePickerComponent.vue'
import FormField from '@/modules/core/components/utils/FormField.vue'
import { useRules } from '@/modules/core/composables/useRulesComposable'
import type DentalRelationshipType from '@/modules/dental/common/models/DXP/DentalRelationshipType'
import { useErrorManagerComposable } from '@/modules/core/composables/errorManagerComposable'
import type Configurations from '../../models/APP/Configuration'
import { getRoleMinMaxAge } from '@/modules/core/composables/minMaxAgeComposable'

const { t } = useI18n()
const { required, validateDate, tooOld, validateActuarialAge, tooYoung, notFutureDate } = useRules()

interface PropsInterface {
  forWhom: string
  startDate: Date
  useBirthDateActuarial?: boolean
  relationships?: DentalRelationshipType[]
  configurations: Configurations
}
const props = withDefaults(defineProps<PropsInterface>(), {})
const errorManager = useErrorManagerComposable()

const insureds = defineModel<Insured[] | any[]>({ default: [] }) //Insured[] throws a type error because members with '_' are lost with the use of reactivity

const { startDate } = props
const validationRules = {
  required,
  validateDate,
  tooOld: (maxAge: number) => (value: string) =>
    props.useBirthDateActuarial
      ? validateActuarialAge({ date: value, maxAge })
      : tooOld(value, maxAge, startDate, 'common.validations.max-age-dental'),
  tooYoung: (minAge: number, message?: string) => (value: string) =>
    props.useBirthDateActuarial
      ? validateActuarialAge({ date: value, minAge })
      : tooYoung(value, minAge, new Date(), message),
  notFutureDate,
  startDateRequired: () => required(startDate, 'common.validations.start-date')
}

function rulesToApply(role: UserRoleEnum): ValidationRuleType[] {
  const ages = getRoleMinMaxAge(props.configurations, role)

  if ([UserRoleEnum.HOLDER_INSURED, UserRoleEnum.HOLDER_NOT_INSURED].includes(role)) {
    return [
      validationRules.required,
      validationRules.validateDate,
      validationRules.tooOld(Number(ages.max)),
      validationRules.tooYoung(Number(ages.min)),
      validationRules.startDateRequired
    ]
  }

  return [
    validationRules.required,
    validationRules.validateDate,
    validationRules.tooYoung(Number(ages.min), 'common.validations.min-age-insured'),
    validationRules.tooOld(Number(ages.max)),
    validationRules.notFutureDate
  ]
}

function birthDateStatement(insured: Insured, index: number) {
  try {
    if (props.forWhom === ForWhomEnum.ME) {
      return t(`rater.common.more-info.birth-date.span-list.me`)
    }
    const i18nTag = insured.isHolder ? 'holder' : 'not-holder-no-title'
    const insuredCount = insureds.value.length > 1 ? index + 1 : null

    const text = t(`rater.common.more-info.birth-date.span-list.${i18nTag}`, {
      index: insuredCount
    })
    if (insured.isHolder || !props.relationships) {
      return text
    }

    const relationship = props.relationships.find((r) => r.id === insured.relationship)

    return relationship?.text ? `${text} (${relationship.text})` : text
  } catch (error) {
    errorManager.handleErrorInsight(error)
  }
}
</script>
